import { useState } from "react";
import { Mail, Phone } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";

export default function Contact() {
  const { settings } = useStore();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/inquiries", { method: "POST", body: { ...form, product_id: "contact" } });
      setSent(true);
    } catch { /* keep form */ } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-candy-mist min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
        <Seo title="Contact" description="Questions, color ideas or just yarn love — get in touch with Hooked by Rachel." path="/contact" />
        <Reveal className="text-center">
          <h1 data-testid="contact-heading" className="font-display font-semibold text-3xl sm:text-4xl">Say hello!</h1>
          <p className="text-ink/70 font-semibold mt-3">Questions, color ideas or just yarn love — we read every note.</p>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <a href={`tel:${(settings.phone || "").replace(/[^0-9+]/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-3 bg-white rounded-3xl border border-candy-soft/70 p-5 hover:shadow-card transition-shadow duration-200">
            <span className="w-11 h-11 rounded-full bg-candy-soft flex items-center justify-center"><Phone className="w-5 h-5 text-candy" /></span>
            <div><p className="font-bold">Call us</p><p className="text-muted text-sm font-semibold">{settings.phone || "310-881-5155"}</p></div>
          </a>
          <div className="flex items-center gap-3 bg-white rounded-3xl border border-candy-soft/70 p-5">
            <span className="w-11 h-11 rounded-full bg-candy-soft flex items-center justify-center"><Mail className="w-5 h-5 text-candy" /></span>
            <div><p className="font-bold">Email</p><p className="text-muted text-sm font-semibold">Use the form — mailbox setup is on the owner's checklist</p></div>
          </div>
        </div>
        {sent ? (
          <div className="mt-8 bg-white rounded-cloud shadow-card p-10 text-center" data-testid="contact-success">
            <p className="text-5xl mb-3" aria-hidden="true">💌</p>
            <h2 className="font-display font-semibold text-2xl">Message sent!</h2>
            <p className="text-muted font-semibold mt-2">Thanks for reaching out — we'll get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="mt-8 bg-white rounded-cloud shadow-card p-6 sm:p-8 space-y-4">
            <input required data-testid="contact-name-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold" />
            <input required type="email" data-testid="contact-email-input" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold" />
            <textarea required rows={4} data-testid="contact-message-input" placeholder="Your message…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold resize-none" />
            <button disabled={busy} data-testid="contact-submit" className="w-full py-3.5 rounded-full bg-candy hover:bg-candy-deep disabled:opacity-60 text-white font-bold shadow-pink transition-colors duration-200">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
